import React, { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import MCard from "components/Shared/MCard.tsx";
import { DATA_TYPE_BOOLEAN, DATA_TYPE_DATE_TIME_DOW, DATA_TYPE_DATE_TIME, DATA_TYPE_DATE_DOW, DATA_TYPE_STRING, DATA_TYPE_MULTI_LINE_STRING } from "typeConstants/dataTypes";
import { caseTypes } from "components/Utility";
import { getETA, updateETA, TYPE_CODE_APPRAISER_DELIVERY, TYPE_CODE_FIELD_INSPECTION } from "requests/caseETA";
import { dateTimeToString } from "components/Shared/Formatters";
import { userHasRight } from "selectors/rights";
import { APPRAISAL_ETA_EDIT, CASE_ETA_VIEW, FIELD_INSPECTION_ETA_EDIT, CASE_APPOINTMENT_DATE_EDIT } from "typeConstants/rights";
import { getAppraisalAppointment, addAppraisalAppointment, updateAppointmentNote, removeAppointment } from "requests/caseAppointment/index.tsx";

const AppointmentInformation = props => {
  const [appraiserDeliveryETA, setAppraiserDeliveryETA] = useState();
  const [fetchingAppraiserDeliveryETA, setFetchingAppraiserDeliveryETA] = useState(false);
  const [fieldInspectionETA, setFieldInspectionETA] = useState();
  const [fetchingFieldInspectionETA, setFetchingFieldInspectionETA] = useState(false);
  const [appraisalAppointment, setAppraisalAppointment] = useState();
  const [fetchingAppraisalAppointment, setFetchingAppraisalAppointment] = useState(false);
  const mounted = useRef(true);

  const isValuation = props.caseType === caseTypes.valuation;

  useEffect(() => {
    if (props.showETA) {
      if (isValuation) {
        getAppraiserDeliveryETA();
        getAppointmentForAppraisal();
      }

      getFieldInspectionETA();
    }
    return () => {
      mounted.current = false;
    };
  }, []);

  const fields = [
    {
      key: "appointmentRequired",
      label: "Field Appointment Required",
      dataType: DATA_TYPE_BOOLEAN,
      value: props.appointmentRequired,
      editable: false
    },
    {
      key: "appointmentDateTime",
      label: "Field Appointment Date",
      dataType: DATA_TYPE_DATE_TIME_DOW,
      value: props.appointmentDateTime,
      editable: false
    },
    {
      key: "appointmentNotes",
      label: "Field Appointment Notes",
      value: props.appointmentNotes,
      editable: false
    },
    {
      key: "appointmentChanged",
      label: "Field Appointment Changed",
      dataType: DATA_TYPE_BOOLEAN,
      value: props.appointmentChanged,
      editable: false
    },
  ];
  if (props.preferredAppointment) {
    fields.push(
      {
        key: "preferredAppointment",
        label: "Preferred Appointment",
        dataType: DATA_TYPE_STRING,
        value: props.preferredAppointment,
        editable: false,
      }
    );
  }
  if (props.showETA) {
    fields.push(
      {
        key: "fieldInspectionETA",
        label: "Field Inspection ETA",
        dataType: DATA_TYPE_DATE_TIME,
        value: fetchingFieldInspectionETA ? "Loading... " : fieldInspectionETA,
        editable: props.isFieldInspectionETAEditable,
        dataTypeOptions: {
          minDate: new Date()
        }
      }
    );
  }
  if (props.inspectorDueDate) {
    fields.push(
      {
        key: "inspectorDueDate",
        label: "FR Due Date",
        dataType: DATA_TYPE_DATE_DOW,
        value: props.inspectorDueDate,
        editable: false,
        dataTypeOptions: {
          minDate: new Date()
        }
      }
    );
  }
  if (isValuation) {
    fields.push(
      {
        key: "appraisalAppointmentDate",
        label: "Appraisal Appointment Date",
        dataType: DATA_TYPE_DATE_TIME,
        value: fetchingAppraisalAppointment
          ? "Loading... "
          : appraisalAppointment?.appointmentDate
            ? appraisalAppointment?.appointmentDate
            : null,
        editable: props.canEditAppointment,
        dataTypeOptions: {
          minDate: new Date()
        }
      },
      {
        key: "appraisalAppointmentNote",
        label: "Appraisal Appointment Note",
        dataType: DATA_TYPE_MULTI_LINE_STRING,
        value: fetchingAppraisalAppointment ? "Loading... " : appraisalAppointment?.appointmentNote,
        editable: props.canEditAppointment
      }
    );
    if (props.showETA) {
      fields.push(
        {
          key: "appraisalDeliverETA",
          label: "Appraisal Delivery ETA",
          dataType: DATA_TYPE_DATE_TIME,
          value: fetchingAppraiserDeliveryETA ? "Loading... " : appraiserDeliveryETA,
          editable: props.isAppraiserETAEditable,
          dataTypeOptions: {
            minDate: new Date()
          }
        }
      );
    }
  }

  const getAppraiserDeliveryETA = () => {
    setFetchingAppraiserDeliveryETA(true);
    getETA(props.controlNumber, TYPE_CODE_APPRAISER_DELIVERY,
      (response) => {
        setAppraiserDeliveryETA(dateTimeToString(response));
        setFetchingAppraiserDeliveryETA(false);
      },
      () => {
        setFetchingAppraiserDeliveryETA(false);
      },
      mounted);
  };

  const getFieldInspectionETA = () => {
    setFetchingFieldInspectionETA(true);
    getETA(props.controlNumber, TYPE_CODE_FIELD_INSPECTION,
      (response) => {
        setFieldInspectionETA(dateTimeToString(response));
        setFetchingFieldInspectionETA(false);
      },
      () => {
        setFetchingFieldInspectionETA(false);
      },
      mounted);
  };

  const onAppointmentSuccess = response => {
    setAppraisalAppointment(response);
    setFetchingAppraisalAppointment(false);
  };

  const onAppointmentError = () => {
    setFetchingAppraisalAppointment(false);
  };

  const getAppointmentForAppraisal = () => {
    setFetchingAppraisalAppointment(true);
    getAppraisalAppointment(props.controlNumber, onAppointmentSuccess, onAppointmentError, mounted);
  };

  const handleSave = (arr, obj) => {
    if (Object.keys(obj).includes("appraisalDeliverETA")) {
      updateETA(props.controlNumber, TYPE_CODE_APPRAISER_DELIVERY, obj.appraisalDeliverETA, getAppraiserDeliveryETA, mounted);
    }
    if (Object.keys(obj).includes("fieldInspectionETA")) {
      updateETA(props.controlNumber, TYPE_CODE_FIELD_INSPECTION, obj.fieldInspectionETA, getFieldInspectionETA, mounted);
    }
    // If the appointment date changes, we add a new appointment record to keep a history
    if (Object.keys(obj).includes("appraisalAppointmentDate")) {
      // Appointment deleted
      if (obj.appraisalAppointmentDate == null) {
        removeAppointment(props.controlNumber, appraisalAppointment.id, getAppointmentForAppraisal, mounted);
        return;
      }
      let payload = {
        appointmentDate: obj.appraisalAppointmentDate?.slice(0, 19), // Remove time zone offset
        appointmentNote: obj.appraisalAppointmentNote
      };
      addAppraisalAppointment(props.controlNumber, payload, getAppointmentForAppraisal, mounted);
    }
    // If only the note is changed, we'll update the existing record
    if (Object.keys(obj).includes("appraisalAppointmentNote")
      && !Object.keys(obj).includes("appraisalAppointmentDate")) {
      let payload = {
        appointmentId: appraisalAppointment.id,
        appointmentNote: obj.appraisalAppointmentNote
      };
      updateAppointmentNote(props.controlNumber, payload, getAppointmentForAppraisal, mounted);
    }
  };

  return (
    <MCard
      title="Appointment Information"
      fields={fields}
      isEditable={props.isAppraiserETAEditable || props.isFieldInspectionETAEditable}
      onEditFinished={handleSave}
    />
  );
};

AppointmentInformation.propTypes = {
  appointmentDateTime: PropTypes.string,
  appointmentRequired: PropTypes.bool,
  appointmentNotes: PropTypes.string,
  appointmentChanged: PropTypes.bool,
  inspectorDueDate: PropTypes.string,
  appraisalDeliverETA: PropTypes.string,
  isAppraiserETAEditable: PropTypes.bool, // User has right to edit Appraisal Delivery ETA
  isFieldInspectionETAEditable: PropTypes.bool, // User has right to edit Field Inspection ETA
  caseType: PropTypes.string,
  controlNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showETA: PropTypes.bool,  // User has right to view case ETAs
  preferredAppointment: PropTypes.string,
  canEditAppointment: PropTypes.bool, // User has right to edit appraiser appointment
};

const mapStateToProps = state => {
  return {
    isAppraiserETAEditable: userHasRight(APPRAISAL_ETA_EDIT)(state),
    isFieldInspectionETAEditable: userHasRight(FIELD_INSPECTION_ETA_EDIT)(state),
    showETA: userHasRight(CASE_ETA_VIEW)(state),
    canEditAppointment: userHasRight(CASE_APPOINTMENT_DATE_EDIT)(state),
  };
};

export default connect(mapStateToProps)(AppointmentInformation);
